import { ZuiBaseElement } from '@zywave/zui-base';
import { updateCustomState } from '@zywave/zui-base/dist/utils/update-custom-state.js';
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { style } from './zui-icon-css.js';
import '@zywave/zui-svg';
import { ensureSVGLoaded } from './zui-icons-loader.js';
import type { PropertyValues } from 'lit';

/**
 * `<zui-icon>` gives consumers ability to leverage our ZUI defined stickersheet of SVG icons.
 *
 * @element zui-icon
 *
 * @cssprop [--zui-icon-size=1.5rem (24px)] - To customize size of icon, assign a new value to this custom property; this property applies to both width and height, therefore component assumes icon is of square aspect ratio
 * @cssprop [--zui-icon-color=currentColor] - To change fill/color of icon that is colorized by default, assign a new value to this custom property
 *
 * @event load - Event fires upon `zui-icon` completing its load and render
 *
 * @cssState loaded - Applied when icon is loaded
 */
export class ZuiIcon extends ZuiBaseElement {
  /**
   * Specify a value to choose an icon, see 'Usage' tab for available icons
   */
  @property({ type: String, attribute: 'icon' })
  icon = '';

  /**
   * Set to true to revert the default `zui-icon` fill of a colorized icon to the inherited font color, which is typically `var(--zui-gray-800)`. Has no effect on non-colorized icons.
   */
  @property({ type: Boolean, attribute: 'no-color' })
  removeIconColor = false;

  static get styles() {
    return [super.styles, style];
  }

  #internals: ElementInternals | null = null;
  #iconLoaded = false;

  constructor() {
    super();

    this.#internals = this.attachInternals?.();
  }

  async updated(changedProps: PropertyValues) {
    if (changedProps.has('icon')) {
      this.#iconLoaded = false;
      this.requestUpdate();
      await ensureSVGLoaded(this.icon);
      this.#iconLoaded = true;
      this.requestUpdate();
      await this.updateComplete;
      this.dispatchEvent(
        new CustomEvent('load', {
          bubbles: true,
          detail: {
            svgLoaded: true,
          },
        })
      );

      updateCustomState(this.#internals, 'add', 'loaded');
    }
  }

  render() {
    if (!this.icon) {
      return html` <style>
        :host {
          display: none;
        }
      </style>`;
    }
    const classes = { 'zui-icon-color': this.removeIconColor };
    return html`
      ${this.#iconLoaded ? html` <zui-svg class="${classMap(classes)}" svg-id="${this.icon}"></zui-svg> ` : html``}
    `;
  }
}

window.customElements.define('zui-icon', ZuiIcon);

declare global {
  interface HTMLElementTagNameMap {
    'zui-icon': ZuiIcon;
  }
}
